import React, { useMemo, useRef } from "react";
import _ from 'lodash';
import cx from 'classnames';

import { AssetCard, PlaceholderAssetCard } from "../card";

import { AssetLayoutProps } from "../../../types";
import Button from "../../../../../components/button";

import styles from './layout.module.scss';
import { ScrollableContainer } from "src/components/scroller";
import { rectSortingStrategy, SortableContext } from "@dnd-kit/sortable";
import { useDndMonitor } from "@dnd-kit/core";
import { nanoid } from "nanoid";
import useMeasure from "react-use-measure";
import { USER_DEFINED } from "src/constants/collections";

const CarouselLayout: React.FC<AssetLayoutProps> = ({ loading, assets, menuState, onMenuChange, onClick, onRemove, onMove, onMoveToSection, collection , section }) => {
  const idRef = useRef(nanoid(6));
  const [carouselRef, carouselBounds] = useMeasure();

  const editing = menuState.primaryAction === 'edit';
  const showPlaceholder = loading && _.size(assets) === 0;
  const empty = !loading && _.size(assets) === 0;
  const canEdit = useMemo(() => {
    return collection?.type == USER_DEFINED && collection?.canEdit;
  }, [collection]);

  useDndMonitor({
    onDragEnd(event) {
      const { active, over } = event;
      const sourceContainer = _.get(active, 'data.current.sortable.containerId');
      const asset = _.get(active, 'data.current.asset');
      const assetIndex = _.get(active, 'data.current.sortable.index');
      const section = _.get(over, 'data.current.section');
      const target = _.get(over, 'data.current.asset');

      if (idRef.current === sourceContainer && asset) {
        if (section) {
          onMoveToSection(section.index, asset);
        } else if (target) {
          onMove(assetIndex, target.order);
        }
      }
    }
  });

  const handleAssetRemove = (asset) => {
    if (onRemove) {
      onRemove(asset);
    }
  }

  return (
    <div className={styles.carousel} ref={carouselRef}>
      {showPlaceholder &&
        <div className={styles.placeholders}>
          {_.times(4, (index) => {
            return <PlaceholderAssetCard key={index} />
          })}
        </div>
      }

      {empty && <EmptyAssets canEdit={canEdit || false} onMenuChange={onMenuChange} />}

      {!showPlaceholder && _.size(assets) > 0 &&
        <SortableContext id={idRef.current} items={assets.map(asset => asset.id || '')} strategy={rectSortingStrategy}>
          <ScrollableContainer distance={carouselBounds.width * 0.8 || 1000} leftArrowComponent={LeftArrow} rightArrowComponent={RightArrow}>
            <div className={styles.assets}>
              {assets.map((asset) => {
                return (
                  <AssetCard
                    key={asset.id}
                    asset={asset}
                    editable={editing}
                    onClick={onClick}
                    onRemove={handleAssetRemove}
                    collection={collection}
                    sectionIndex ={section}
                  />
                )
              })}
            </div>
          </ScrollableContainer>
        </SortableContext>
      }
    </div >
  );
}

const LeftArrow: React.FC<any> = () => {
  return (
    <div className={cx(styles.arrow, styles.left)}>
      <i className="paperflite-arrow-up"></i>
    </div>
  );
}

const RightArrow: React.FC<any> = () => {
  return (
    <div className={cx(styles.arrow, styles.right)}>
      <i className="paperflite-arrow-up"></i>
    </div>
  );
}

interface EmptyAssetsProps {
  canEdit: boolean;
  // eslint-disable-next-line no-unused-vars
  onMenuChange: (state: any) => void;
}

const EmptyAssets: React.FC<EmptyAssetsProps> = ({ canEdit, onMenuChange }) => {

  const handleAddAsset = () => {
    onMenuChange({
      primaryAction: 'edit',
      secondaryAction: 'add-asset',
    });
  }
  
  return (
    <div className={styles.empty}>
      <div className={cx(styles.card, styles.image)}>
        <img src="/images/collections/empty.svg" />
      </div>
      <div className={cx(styles.card, styles.message)}>
        <p>It’s so empty here, let’s make this collection interesting...</p>
        {canEdit && <Button variant={'primary'} icon="fa-solid fa-pencil" text="Edit" onClick={handleAddAsset} />}
      </div>
      <div className={cx(styles.card)}></div>
    </div>
  );
}

export { CarouselLayout };